/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UpdateFulfillmentDetailsPayload } from 'common/types/redux/orders';
import { UpdateKeyValuePairPayload } from 'common/stores/redux/interfaces';
import { ORDER_FULFILLMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    DocumentContact,
    GenericRecord,
    Order,
    Comment,
    FolderAttachment,
} from 'common/types';

interface State {
    initialized: boolean;
    filesInitialized: boolean;
    historyInitialized: boolean;
    order: Order | null;
    finances?: unknown;
    snapshot?: unknown;
}

const initialState: State = {
    initialized: false,
    order: null,
    filesInitialized: false,
    historyInitialized: false,
    finances: undefined,
    snapshot: undefined,
};

const clientOrderSlice = createSlice({
    name: 'clientOrder',
    initialState,
    reducers: {
        initialize: (state: State, action: PayloadAction<Order>) => {
            state.initialized = true;
            state.filesInitialized = false;
            state.historyInitialized = false;
            state.order = action.payload;
        },

        setOrder: (state: State, action: PayloadAction<Order>) => {
            state.order = action.payload;
        },

        updateOrder: (state: State, action: PayloadAction<Order>) => {
            if (!state.order) {
                return;
            }

            state.order = {
                ...state.order,
                ...action.payload,
            };
        },

        resetPages: (state: State) => {
            state.initialized = false;
            state.filesInitialized = false;
            state.historyInitialized = false;
        },

        initializeRoute: (state: State, action: PayloadAction<string>) => {
            return {
                ...state,
                [`${action.payload}Initialized`]: true,
            };
        },

        updateKeyValuePair: (
            state: State,
            action: PayloadAction<UpdateKeyValuePairPayload>
        ) => {
            if (!state.order) {
                return;
            }

            const { key, value } = action.payload;
            const update: GenericRecord<unknown> = {};

            if (Array.isArray(key)) {
                for (let idx = 0; idx < key.length; idx++) {
                    update[key[idx]] = value[idx];
                }
            } else {
                update[key] = value;
            }

            state.order = { ...state.order, ...update };
        },

        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.order) {
                return;
            }

            state.order = {
                ...state.order,
                instructions: {
                    special: action.payload,
                },
            };
        },

        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.order) {
                return;
            }

            state.order.contacts = [
                ...(state.order.contacts ?? []),
                action.payload,
            ];
        },

        removeContact: (state: State, action: PayloadAction<number>) => {
            if (!state.order) {
                return;
            }

            state.order.contacts = state.order.contacts?.filter(
                contact => contact.id !== action.payload
            );
        },

        addComment: (state: State, action: PayloadAction<Comment>) => {
            if (!state.order) {
                return;
            }

            const currentComments = [...(state.order.comments ?? [])];
            const commentToAdd = action.payload;

            state.order = {
                ...state.order,
                comments: [...currentComments, commentToAdd],
            };
        },

        updateComment: (state: State, action: PayloadAction<Comment>) => {
            if (!state.order) {
                return;
            }

            const currentComments = [...(state.order.comments ?? [])];
            const updatedComment = action.payload;

            const updatedCommentIndex = currentComments.findIndex(
                c => c.id === updatedComment.id
            );

            if (updatedCommentIndex !== -1) {
                currentComments[updatedCommentIndex] = updatedComment;

                state.order = {
                    ...state.order,
                    comments: currentComments,
                };
            }
        },

        addFolderAttachment: (
            state: State,
            action: PayloadAction<FolderAttachment>
        ) => {
            if (!state.order?.folder) {
                return;
            }

            const currentFolderAttachments = [
                ...(state.order.folder.folderAttachments ?? []),
            ];

            state.order = {
                ...state.order,
                folder: {
                    ...state.order.folder,
                    folderAttachments: [
                        ...currentFolderAttachments,
                        action.payload,
                    ],
                },
            };
        },

        removeFolderAttachment: (
            state: State,
            action: PayloadAction<number>
        ) => {
            if (!state.order?.folder) {
                return;
            }

            const filteredAttachments = (
                state.order.folder.folderAttachments ?? []
            ).filter(attachment => attachment.id !== action.payload);

            state.order = {
                ...state.order,
                folder: {
                    ...state.order.folder,
                    folderAttachments: filteredAttachments,
                },
            };
        },

        updateFulfillmentDetails: (
            state: State,
            action: PayloadAction<UpdateFulfillmentDetailsPayload>
        ) => {
            const { fulfillmentDetails, fulfillmentDistributionCenter } =
                action.payload;

            if (
                !state.order ||
                (!fulfillmentDetails && !fulfillmentDistributionCenter)
            ) {
                return;
            }

            const {
                fulfillmentTypeId,
                fulfillmentDistributionCenterId,
                distributionCenterId,
                fulfillmentToAddress = '',
                fulfillmentToCity = '',
                fulfillmentToRegion = '',
                fulfillmentToPostalCode = '',
                fulfillmentTo = '',
                residentialDelivery = false,
            } = fulfillmentDetails;

            state.order = {
                ...state.order,
                fulfillmentTypeId: fulfillmentTypeId as ORDER_FULFILLMENT_TYPE,
                fulfillmentDistributionCenterId:
                    fulfillmentDistributionCenterId ||
                    distributionCenterId ||
                    0,
                fulfillmentToAddress,
                fulfillmentToCity,
                fulfillmentToRegion,
                fulfillmentToPostalCode,
                fulfillmentTo,
                residentialDelivery,
                vendorDistributionCenter: fulfillmentDistributionCenter,
            };
        },
    },
});

export const {
    initialize,
    updateOrder,
    setOrder,
    resetPages,
    initializeRoute,
    updateKeyValuePair,
    updateSpecialInstructions,
    addContact,
    removeContact,
    addComment,
    updateComment,
    addFolderAttachment,
    removeFolderAttachment,
    updateFulfillmentDetails,
} = clientOrderSlice.actions;

export default clientOrderSlice.reducer;
